import { ANIMATION_LIFECYCLE_EVENTS, NAVIGATION_ANIMATION_PHASES } from './animation_lifecycle_events'
import { OVERLAY_KINDS, OVERLAY_LIFECYCLE_EVENTS, OVERLAY_PHASES } from '../../overlay_lifecycle/overlay_lifecycle_events'
import { SCOPES } from '../../scope/scope'

let navigationTransitionId = 0
let delayedCleanupIds = {}

let navigationState = {
	phase: NAVIGATION_ANIMATION_PHASES.IDLE,
	transitionId: 0,
	scope: SCOPES.NORMAL_PAGE,
	startedAt: 0,
	updatedAt: 0,
}

let overlayState = {
	project: {
		kind: OVERLAY_KINDS.PROJECT,
		phase: OVERLAY_PHASES.HIDDEN,
		transitionId: 0,
		id: null,
		type: null,
		link: null,
		title: null,
		startedAt: 0,
		updatedAt: 0,
	},
	page: {
		kind: OVERLAY_KINDS.PAGE,
		phase: OVERLAY_PHASES.HIDDEN,
		transitionId: 0,
		id: null,
		type: null,
		link: null,
		title: null,
		startedAt: 0,
		updatedAt: 0,
	},
}

const now = function () {
	if (typeof performance !== 'undefined' && performance.now) {
		return performance.now()
	}
	return Date.now()
}

const cloneState = function (state) {
	return Object.assign({}, state)
}

const cloneOverlayState = function () {
	return {
		project: cloneState(overlayState.project),
		page: cloneState(overlayState.page),
	}
}

const emitLifecycleEvent = function (eventName, detail) {
	if (typeof window === 'undefined') {
		return
	}

	if (window.laytheme && typeof window.laytheme.emit === 'function') {
		window.laytheme.emit(eventName, detail)
	}

	if (typeof window.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
		window.dispatchEvent(new CustomEvent('lay:' + eventName, { detail: detail }))
	}
}

const setNavigationPhase = function (phase, options = {}) {
	let transitionId = options.transitionId || navigationState.transitionId
	const startsNewNavigation =
		phase == NAVIGATION_ANIMATION_PHASES.OUT_STARTED ||
		(phase == NAVIGATION_ANIMATION_PHASES.OUT_ENDED &&
			(navigationState.phase == NAVIGATION_ANIMATION_PHASES.IDLE || navigationState.phase == NAVIGATION_ANIMATION_PHASES.STABLE))

	if (startsNewNavigation) {
		transitionId = options.transitionId || navigationTransitionId + 1
		navigationTransitionId = transitionId
	}

	if (!transitionId) {
		transitionId = navigationTransitionId
	}

	const timestamp = now()
	navigationState = {
		phase: phase,
		transitionId: transitionId,
		scope: options.scope || navigationState.scope,
		startedAt: startsNewNavigation ? timestamp : navigationState.startedAt,
		updatedAt: timestamp,
	}

	const detail = cloneState(navigationState)
	emitLifecycleEvent(ANIMATION_LIFECYCLE_EVENTS.NAVIGATION_PHASE_CHANGED, detail)
	return detail
}

const getNavigationState = function () {
	return cloneState(navigationState)
}

const beginDelayedCleanup = function (key = 'default') {
	const nextId = (delayedCleanupIds[key] || 0) + 1
	delayedCleanupIds[key] = nextId
	return nextId
}

const isDelayedCleanupCurrent = function (cleanupId, key = 'default') {
	return cleanupId === delayedCleanupIds[key]
}

const isNavigationAnimating = function () {
	return [
		NAVIGATION_ANIMATION_PHASES.OUT_STARTED,
		NAVIGATION_ANIMATION_PHASES.DOM_REPLACING,
		NAVIGATION_ANIMATION_PHASES.IN_PREPARING,
		NAVIGATION_ANIMATION_PHASES.IN_STARTED,
	].indexOf(navigationState.phase) !== -1
}

const isNavigationStable = function () {
	return navigationState.phase == NAVIGATION_ANIMATION_PHASES.IDLE || navigationState.phase == NAVIGATION_ANIMATION_PHASES.STABLE
}

const getOverlayState = function () {
	return cloneOverlayState()
}

const isOverlayPhaseOpen = function (phase) {
	return (
		phase == OVERLAY_PHASES.LOADING ||
		phase == OVERLAY_PHASES.OPENING ||
		phase == OVERLAY_PHASES.VISIBLE ||
		phase == OVERLAY_PHASES.CLOSING ||
		phase == OVERLAY_PHASES.DESTROYING
	)
}

const isAnyOverlayOpen = function () {
	return isOverlayPhaseOpen(overlayState.project.phase) || isOverlayPhaseOpen(overlayState.page.phase)
}

const isAnyOverlayAnimating = function () {
	return (
		overlayState.project.phase == OVERLAY_PHASES.LOADING ||
		overlayState.project.phase == OVERLAY_PHASES.OPENING ||
		overlayState.project.phase == OVERLAY_PHASES.CLOSING ||
		overlayState.project.phase == OVERLAY_PHASES.DESTROYING ||
		overlayState.page.phase == OVERLAY_PHASES.LOADING ||
		overlayState.page.phase == OVERLAY_PHASES.OPENING ||
		overlayState.page.phase == OVERLAY_PHASES.CLOSING ||
		overlayState.page.phase == OVERLAY_PHASES.DESTROYING
	)
}

// ─────────────────────────────────────────────────────────────────────────
// Intro-pending state (Phase 3 step 1)
//
// Three flags previously lived as module-level `let`s in scrub.js and
// textanimations.js, set/cleared in event handlers and read from various
// `if`-conditions. They tracked closely-related ideas:
//
//   • introPendingByScope[scope]    — scrub.js used to hold scrub pairings
//                                     at progress 0 until intro animation
//                                     fires (refreshPairings({...,
//                                     animateInitialProgress: true})).
//   • overlayNavigationRevealPending — textanimations.js used to make
//                                     `playAnimation` bail for nav-synced
//                                     overlay text-animations until
//                                     `transition_navigation_in_completed`.
//
// Centralising them here makes the cross-module shared lifecycle visible
// at one address. Future steps can move the event listeners themselves
// into this controller; for now the listeners stay in their original
// modules but go through this API instead of toggling local `let`s.
// ─────────────────────────────────────────────────────────────────────────

let introPendingByScope = { overlay: false, normal: false }
let overlayNavigationRevealPending = false

const setIntroPending = function (scope, pending) {
	if (scope !== 'overlay' && scope !== 'normal') return
	introPendingByScope[scope] = !!pending
}

const isIntroPendingFor = function (scope) {
	return !!introPendingByScope[scope]
}

const isAnyIntroPending = function () {
	return !!(introPendingByScope.overlay || introPendingByScope.normal)
}

const setOverlayNavigationRevealPending = function (pending) {
	overlayNavigationRevealPending = !!pending
}

const isOverlayNavigationRevealPending = function () {
	return overlayNavigationRevealPending
}

// ─────────────────────────────────────────────────────────────────────────
// Browser-tab visibility gate
//
// IntersectionObserver, requestAnimationFrame, and timers do not all pause in
// the same way while a page loads in a hidden browser tab. If one animation
// system advances while another is throttled, switching back to the tab shows
// only a subset of animations playing. Animation modules use this gate to
// defer their first real playback / simulation work until the document is
// visible, so all intro animations share the same visible start.
// ─────────────────────────────────────────────────────────────────────────

let documentVisibleCallbacks = []
let documentVisibilityListenerInstalled = false

const isDocumentVisible = function () {
                    
	// Dev builds never gate intro animations on tab visibility: while
	// developing, the live site usually sits in a background tab during a
	// rebuild, and we still want the page to fade in on reload. This block
	// is stripped from prod builds (see the ifdef strip in buildFrontendJS),
	// so production keeps the real visibility gate below.
	return true
          
	if (typeof document === 'undefined' || !document.visibilityState) {
		return true
	}
	return document.visibilityState !== 'hidden'
}

const flushDocumentVisibleCallbacks = function () {
	if (!isDocumentVisible() || documentVisibleCallbacks.length === 0) {
		return
	}
	const callbacks = documentVisibleCallbacks
	documentVisibleCallbacks = []
	callbacks.forEach((callback) => {
		try {
			callback()
		} catch (err) {
			setTimeout(() => {
				throw err
			}, 0)
		}
	})
}

const installDocumentVisibilityListener = function () {
	if (documentVisibilityListenerInstalled || typeof document === 'undefined') {
		return
	}
	documentVisibilityListenerInstalled = true
	document.addEventListener('visibilitychange', flushDocumentVisibleCallbacks)
	if (typeof window !== 'undefined') {
		window.addEventListener('pageshow', flushDocumentVisibleCallbacks)
	}
}

const runWhenDocumentVisible = function (callback) {
	if (typeof callback !== 'function') {
		return false
	}
	if (isDocumentVisible()) {
		callback()
		return false
	}
	installDocumentVisibilityListener()
	documentVisibleCallbacks.push(callback)
	return true
}

// ─────────────────────────────────────────────────────────────────────────
// Phase 3 second pass: own the SET-TRUE side of intro-pending lifecycle.
//
// scrub.js and textanimations.js used to BOTH listen on
// `project_overlay_before_animate_in` (and friends) and toggle their own
// flag through the controller's setters. Moving the SET-TRUE listeners
// into this module means consumers stop duplicating "what does this event
// mean for the lifecycle?" interpretations across files.
//
// SET-FALSE deliberately stays in the consumer modules — it's order-
// coupled with their cleanup work (e.g. scrub.js's
// `transition_navigation_in_completed` handler reads the pending flags
// to decide which `refreshPairings({...})` branch to take, then clears
// the flags after acting). Centralising SET-FALSE here would force us to
// either delay it (race window) or split the decision across modules.
// ─────────────────────────────────────────────────────────────────────────

function hasActiveNavigationTransitionIn() {
	if (typeof window === 'undefined' || typeof window.layData === 'undefined') {
		return false
	}
	return (
		window.layData.navigation_transition_in !== 'none' &&
		parseInt(window.layData.navigation_transition_in_duration, 10) > 0
	)
}

let lifecycleListenersInstalled = false

const installLifecycleEventListeners = function () {
	if (lifecycleListenersInstalled) return
	if (typeof window === 'undefined' || !window.laytheme || typeof window.laytheme.on !== 'function') return
	lifecycleListenersInstalled = true

	// SPA navigation start (non-overlay): set normal-page intro pending if
	// the configured transition will actually animate.
	window.laytheme.on('before_content_replacement', () => {
		setIntroPending('normal', hasActiveNavigationTransitionIn())
	})

	// First-load + project-overlay-open both go through this event in
	// `doTransitionIn` — same intro semantics as `before_content_replacement`
	// for the normal-page scope.
	window.laytheme.on('transition_navigation_in_started', () => {
		if (!hasActiveNavigationTransitionIn()) return
		setIntroPending('normal', true)
	})

	// Project overlay opening (SPA or direct-load): both flags relevant.
	window.laytheme.on('project_overlay_before_animate_in', () => {
		setIntroPending('overlay', true)
		setOverlayNavigationRevealPending(true)
	})

	// SPA navigation that opens a project overlay fires THIS variant of the
	// content-replacement event instead of the plain one above. textanimations
	// used to listen here just to flip the reveal flag — that's now ours.
	window.laytheme.on('before_content_replacement_for_project_overlay', () => {
		setOverlayNavigationRevealPending(true)
	})

	window.laytheme.on(OVERLAY_LIFECYCLE_EVENTS.PHASE_CHANGED, (state) => {
		if (!state || !state.kind) {
			return
		}
		if (state.kind == OVERLAY_KINDS.PROJECT) {
			overlayState.project = cloneState(state)
		} else if (state.kind == OVERLAY_KINDS.PAGE) {
			overlayState.page = cloneState(state)
		}
	})
}

// Auto-install on import. Defensive against load-order issues — the call
// is idempotent and bails early if `window.laytheme` isn't ready yet.
// (Importers also call this implicitly by virtue of importing the
// controller; it will succeed on the first import that happens after
// events_channels.js has run.)
installLifecycleEventListeners()

const animation_lifecycle_controller = {
	setNavigationPhase: setNavigationPhase,
	getNavigationState: getNavigationState,
	getOverlayState: getOverlayState,
	isAnyOverlayOpen: isAnyOverlayOpen,
	isAnyOverlayAnimating: isAnyOverlayAnimating,
	beginDelayedCleanup: beginDelayedCleanup,
	isDelayedCleanupCurrent: isDelayedCleanupCurrent,
	isNavigationAnimating: isNavigationAnimating,
	isNavigationStable: isNavigationStable,
	setIntroPending: setIntroPending,
	isIntroPendingFor: isIntroPendingFor,
	isAnyIntroPending: isAnyIntroPending,
	setOverlayNavigationRevealPending: setOverlayNavigationRevealPending,
	isOverlayNavigationRevealPending: isOverlayNavigationRevealPending,
	isDocumentVisible: isDocumentVisible,
	runWhenDocumentVisible: runWhenDocumentVisible,
	installLifecycleEventListeners: installLifecycleEventListeners,
}

export default animation_lifecycle_controller
